import React from "react";
import EChart from "@src/reporting/components/EChart";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ClearIcon from "@mui/icons-material/Clear";

import { getLocale, rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";

import styles from "./OperatorReport.module.styl";

const pad2 = (value) => String(value).padStart(2, "0");

const fullName = (row) => {
  const name = `${row?.firstName || ""} ${row?.lastName || ""}`.trim();
  return name || row?.nationalId || t("voter.operator");
};

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  return String(value).replace("T", " ").slice(0, 19);
};

const formatTime = (value) => {
  if (!value) {
    return "—";
  }

  const text = String(value);
  const index = text.indexOf("T");
  return index >= 0 ? text.slice(index + 1, index + 9) : text.slice(-8);
};

const formatDateLabel = (dateString) => {
  if (!dateString) {
    return "—";
  }

  const date = new Date(`${dateString}T12:00:00`);
  if (Number.isNaN(date.getTime())) {
    return dateString;
  }

  return new Intl.DateTimeFormat(getLocale(), {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
};

const shiftDate = (dateString, delta) => {
  const date = new Date(`${dateString}T12:00:00`);
  date.setDate(date.getDate() + delta);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const datePart = (value) => String(value || "").slice(0, 10);

const distinctApplications = (applications) => {
  const seen = new Set();
  const rows = [];

  for (const application of applications || []) {
    const ninu = String(application?.nationalIdentifier || "").trim().toUpperCase();
    const key = ninu || `gid:${application?.gid ?? rows.length}`;
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push(application);
  }

  return rows;
};

const statusLabel = (value) => {
  switch (Number(value)) {
    case 1:
      return t("common.submitted");
    case 2:
      return t("common.error");
    case 3:
      return t("common.approved");
    case 4:
      return t("voter.manualAdjudication");
    case 5:
      return t("voter.retransmission");
    case 6:
      return t("voter.duplicate");
    default:
      return value === null || value === undefined ? t("common.unknown") : String(value);
  }
};

const applicationTypeLabel = (value) => {
  switch (Number(value)) {
    case 1:
      return t("voter.standard");
    case 2:
      return t("voter.correction");
    default:
      return value === null || value === undefined ? t("common.unknown") : String(value);
  }
};

const personImageSrc = (row) => {
  if (!row?.defaultImageId) {
    return "";
  }

  return `${getServiceUri()}person-photo/data/${Number(row.defaultImageId)}`;
};

const centerLabel = (center) => {
  const code = String(center?.code || center?.votingCenterCode || "").trim();
  const name = String(center?.name || center?.votingCenterName || "").trim();
  return code && name ? `${code} · ${name}` : code || name || t("reporting.unknownCenter");
};

const fillDailyHistory = (days, firstSubmissionAt, lastSubmissionAt) => {
  const first = datePart(firstSubmissionAt);
  const last = datePart(lastSubmissionAt);

  if (!first || !last) {
    return [];
  }

  const byDate = new Map((days || []).map((row) => [row.date, row]));
  const cursor = new Date(`${first}T12:00:00`);
  const end = new Date(`${last}T12:00:00`);
  const out = [];

  while (cursor <= end) {
    const key = `${cursor.getFullYear()}-${pad2(cursor.getMonth() + 1)}-${pad2(cursor.getDate())}`;
    const row = byDate.get(key);

    out.push({
      date: key,
      count: Number(row?.count || 0),
      standardCount: Number(row?.standardCount || 0),
      correctionCount: Number(row?.correctionCount || 0),
    });

    cursor.setDate(cursor.getDate() + 1);
  }

  return out;
};

const Metric = ({ label, value, detail }) => (
  <div className={styles.metric}>
    <div className={styles.metricLabel}>{label}</div>
    <div className={styles.metricValue}>{value}</div>
    {detail ? <div className={styles.metricDetail}>{detail}</div> : null}
  </div>
);

const OperatorHero = ({ report, gui }) => {
  const src = can(gui, "READ_PERSON_PHOTO") ? personImageSrc(report) : "";

  return (
    <header className={styles.hero}>
      <div className={styles.heroTopRow}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => gui.goTo("/reporting/tablets")}
        >
          <ArrowBackIcon fontSize="small" />
          {t("menu.reporting.tablets")}
        </button>
      </div>

      <div className={styles.heroBody}>
        {report.personGid && can(gui, "READ_PERSON") ? (
          <button
            type="button"
            className={styles.avatarButton}
            title={t("user.openPersonRecord")}
            onClick={() => gui.goTo(`/person/${report.personGid}`)}
          >
            {src ? (
              <img className={styles.avatarImage} src={src} alt={fullName(report)} />
            ) : (
              <AccountCircleOutlinedIcon className={styles.avatarFallback} />
            )}
          </button>
        ) : (
          <div className={styles.avatarButton}>
            {src ? (
              <img className={styles.avatarImage} src={src} alt={fullName(report)} />
            ) : (
              <AccountCircleOutlinedIcon className={styles.avatarFallback} />
            )}
          </div>
        )}

        <div className={styles.heroIdentity}>
          <div className={styles.eyebrow}>{t("reporting.operatorActivityReport")}</div>
          <h1 className={styles.title}>{fullName(report)}</h1>
          <div className={styles.ninu}>NINU {report.nationalId}</div>
        </div>

        <div className={styles.heroSpan}>
          <div className={styles.spanLabel}>{t("reporting.activitySpan")}</div>
          <div className={styles.spanValue}>
            {datePart(report.firstSubmissionAt) || "—"} → {datePart(report.lastSubmissionAt) || "—"}
          </div>
        </div>
      </div>
    </header>
  );
};

const AccountsPanel = ({ accounts, gui }) => (
  <section className={styles.panel}>
    <div className={styles.panelHeader}>
      <div>
        <div className={styles.panelEyebrow}>{t("reporting.identityLinkage")}</div>
        <h2 className={styles.panelTitle}>{t("reporting.accountsAttachedNinu")}</h2>
      </div>
      <span className={styles.countBadge}>{(accounts || []).length}</span>
    </div>

    <div className={styles.accountGrid}>
      {(accounts || []).map((account) => {
        const content = (
          <>
            <div className={styles.accountTop}>
              <span className={styles.accountUsername}>{account.username || `#${account.gid}`}</span>
              <span className={styles.accountStatus}>{account.status || t("common.unknown")}</span>
            </div>
            <div className={styles.accountCenter}>{centerLabel(account)}</div>
          </>
        );

        return can(gui, "READ_USER_ACCOUNT") && account.gid ? (
          <button
            key={account.gid || account.username}
            type="button"
            className={styles.accountCard}
            onClick={() => gui.goTo(`/user-account/${account.gid}`)}
          >
            {content}
          </button>
        ) : (
          <div
            key={account.gid || account.username}
            className={styles.accountCard}
          >
            {content}
          </div>
        );
      })}
    </div>
  </section>
);

const DailyHistory = ({ report, selectedDate, onSelectDate }) => {
  const history = React.useMemo(
    () => fillDailyHistory(report.days, report.firstSubmissionAt, report.lastSubmissionAt),
    [report.days, report.firstSubmissionAt, report.lastSubmissionAt]
  );

  const option = React.useMemo(() => {
    const dates = history.map((row) => row.date);
    const counts = history.map((row) => row.count);

    return {
      animationDuration: 280,
      grid: { left: 48, right: 18, top: 25, bottom: 72 },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (items) => {
          const item = items?.[0];
          return item ? `<strong>${item.axisValue}</strong><br/>${item.value} ${t("reporting.applicantsLower")}` : "";
        },
      },
      xAxis: {
        type: "category",
        data: dates,
        axisTick: { show: false },
        axisLabel: {
          color: "#728097",
          formatter: (value) => value.slice(5),
          hideOverlap: true,
        },
        axisLine: { lineStyle: { color: "#d8e0ec" } },
      },
      yAxis: {
        type: "value",
        minInterval: 1,
        axisLabel: { color: "#728097" },
        splitLine: { lineStyle: { color: "#eef2f7" } },
      },
      dataZoom: [
        { type: "inside", start: Math.max(0, 100 - Math.min(100, 45 / Math.max(1, history.length) * 100)), end: 100 },
        {
          type: "slider",
          height: 20,
          bottom: 18,
          borderColor: "#d8e0ec",
          backgroundColor: "#f4f7fb",
          fillerColor: "rgba(45, 108, 223, .16)",
          handleStyle: { color: "#2d6cdf" },
        },
      ],
      series: [
        {
          type: "bar",
          data: counts.map((count, index) => ({
            value: count,
            itemStyle: {
              color: dates[index] === selectedDate ? "#174ea6" : "#7ea4e6",
              borderRadius: [4, 4, 0, 0],
            },
          })),
          barMaxWidth: 18,
        },
      ],
    };
  }, [history, selectedDate]);

  if (!history.length) {
    return <div className={styles.emptyChart}>{t("reporting.noApplicantActivity")}</div>;
  }

  return (
    <EChart
      option={option}
      className={styles.chartHistory}
      onClick={(params) => {
        const date = params?.name;
        if (date) {
          onSelectDate(date);
        }
      }}
    />
  );
};

const HourlyHistogram = ({ hours, selectedHour, onSelectHour }) => {
  const normalized = Array.from({ length: 24 }, (_, hour) => {
    const row = (hours || []).find((item) => Number(item.hour) === hour);
    return Number(row?.count || 0);
  });

  const option = React.useMemo(() => ({
    animationDuration: 220,
    grid: { left: 45, right: 15, top: 24, bottom: 38 },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (items) => {
        const item = items?.[0];
        return item ? `<strong>${item.axisValue}</strong><br/>${item.value} ${t("reporting.applicantsLower")}` : "";
      },
    },
    xAxis: {
      type: "category",
      data: Array.from({ length: 24 }, (_, hour) => `${pad2(hour)}:00`),
      axisTick: { show: false },
      axisLabel: { color: "#738097", interval: 1, fontSize: 10 },
      axisLine: { lineStyle: { color: "#d8e0ec" } },
    },
    yAxis: {
      type: "value",
      minInterval: 1,
      axisLabel: { color: "#738097" },
      splitLine: { lineStyle: { color: "#eef2f7" } },
    },
    series: [
      {
        type: "bar",
        barMaxWidth: 24,
        data: normalized.map((count, hour) => ({
          value: count,
          itemStyle: {
            color: selectedHour === hour ? "#174ea6" : "#5f8fdc",
            borderRadius: [5, 5, 0, 0],
          },
        })),
      },
    ],
  }), [normalized.join(","), selectedHour]);

  return (
    <EChart
      option={option}
      className={styles.chartDay}
      onClick={(params) => {
        const hour = Number(String(params?.name || "").slice(0, 2));
        if (Number.isFinite(hour)) {
          onSelectHour(selectedHour === hour ? null : hour);
        }
      }}
    />
  );
};

const CadenceChart = ({ applications, gui }) => {
  const option = React.useMemo(() => {
    const points = (applications || []).map((app, index) => ({
      value: [app.submittedAt, index % 5],
      gid: app.gid,
      application: app,
    }));

    return {
      animationDuration: 180,
      grid: { left: 18, right: 18, top: 18, bottom: 36 },
      tooltip: {
        formatter: (params) => {
          const app = params?.data?.application;
          if (!app) {
            return "";
          }
          return [
            `<strong>${formatTime(app.submittedAt)}</strong>`,
            `NINU ${app.nationalIdentifier || "—"}`,
            app.receiptNumber ? `${t("voter.receipt")} ${app.receiptNumber}` : null,
            centerLabel(app),
          ].filter(Boolean).join("<br/>");
        },
      },
      xAxis: {
        type: "time",
        axisLabel: { color: "#738097" },
        splitLine: { show: true, lineStyle: { color: "#f0f3f8" } },
        axisLine: { lineStyle: { color: "#d8e0ec" } },
      },
      yAxis: {
        type: "value",
        min: -1,
        max: 5,
        show: false,
      },
      series: [
        {
          type: "scatter",
          data: points,
          symbolSize: 9,
          itemStyle: { color: "#245fce", opacity: 0.78 },
          emphasis: { scale: 1.7 },
        },
      ],
    };
  }, [applications]);

  if (!applications?.length) {
    return <div className={styles.emptyChart}>{t("reporting.noApplicantsDay")}</div>;
  }

  return (
    <EChart
      option={option}
      className={styles.chartCadence}
      onClick={can(gui, "READ_VOTER_APPLICATION") ? (params) => {
        const gid = params?.data?.gid;
        if (gid) {
          gui.goTo(`/voter-application/${gid}`);
        }
      } : undefined}
    />
  );
};

const CentersChart = ({ rows }) => {
  const data = (rows || []).slice(0, 12);
  const option = React.useMemo(() => ({
    animationDuration: 220,
    grid: { left: 135, right: 30, top: 18, bottom: 28 },
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    xAxis: {
      type: "value",
      minInterval: 1,
      axisLabel: { color: "#738097" },
      splitLine: { lineStyle: { color: "#eef2f7" } },
    },
    yAxis: {
      type: "category",
      inverse: true,
      data: data.map((row) => row.code || row.name || t("common.unknown")),
      axisTick: { show: false },
      axisLabel: { color: "#526079", width: 120, overflow: "truncate" },
      axisLine: { show: false },
    },
    series: [
      {
        type: "bar",
        data: data.map((row) => row.count),
        barMaxWidth: 18,
        itemStyle: { color: "#5f8fdc", borderRadius: [0, 5, 5, 0] },
      },
    ],
  }), [rows]);

  return <EChart option={option} className={styles.chartCompact} />;
};

const StatusChart = ({ rows }) => {
  const data = (rows || []).map((row) => ({
    name: statusLabel(row.statusCode),
    value: Number(row.count || 0),
  }));

  const option = React.useMemo(() => ({
    animationDuration: 220,
    tooltip: { trigger: "item", formatter: "{b}<br/>{c} ({d}%)" },
    legend: { bottom: 0, textStyle: { color: "#637088" } },
    series: [
      {
        type: "pie",
        radius: ["48%", "72%"],
        center: ["50%", "43%"],
        avoidLabelOverlap: true,
        label: { show: false },
        emphasis: { label: { show: true, fontWeight: "bold" } },
        data,
      },
    ],
  }), [rows]);

  return <EChart option={option} className={styles.chartCompact} />;
};

const DayApplications = ({ day, selectedHour, gui, onClearHour }) => {
  const rows = React.useMemo(() => {
    const all = distinctApplications(Array.isArray(day?.applications) ? day.applications : []);
    if (selectedHour === null || selectedHour === undefined) {
      return all;
    }

    return all.filter((row) => {
      const text = String(row.submittedAt || "");
      const hour = Number(text.slice(11, 13));
      return hour === selectedHour;
    });
  }, [day, selectedHour]);

  return (
    <section className={styles.panel}>
      <div className={styles.panelHeader}>
        <div>
          <div className={styles.panelEyebrow}>{t("reporting.drillDown")}</div>
          <h2 className={styles.panelTitle}>
            {t("reporting.applicants")}
            {selectedHour !== null && selectedHour !== undefined
              ? ` · ${pad2(selectedHour)}:00–${pad2(selectedHour)}:59`
              : ""}
          </h2>
        </div>

        <div className={styles.tableHeaderRight}>
          <span className={styles.countBadge}>{rows.length}</span>
          {selectedHour !== null && selectedHour !== undefined ? (
            <Tooltip title={t("reporting.clearHourFilter")}>
              <IconButton size="small" onClick={onClearHour}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </div>
      </div>

      <div className={styles.tableShell}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t("common.time")}</th>
              <th>{t("voter.applicantNinu")}</th>
              <th>{t("voter.receipt")}</th>
              <th>{t("common.type")}</th>
              <th>{t("voter.votingCenter")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? rows.map((row) => (
              <tr key={`${row.gid}-${row.submittedAt}`}>
                <td className={styles.timeCell}>{formatTime(row.submittedAt)}</td>
                <td><span className={styles.applicantNinu}>{row.nationalIdentifier || "—"}</span></td>
                <td>{row.receiptNumber || "—"}</td>
                <td>{applicationTypeLabel(row.applicationType)}</td>
                <td>{centerLabel(row)}</td>
                <td className={styles.actionCell}>
                  {can(gui, "READ_VOTER_APPLICATION") ? (
                    <Tooltip title={t("voter.viewApplication")}>
                      <IconButton
                        size="small"
                        onClick={() => row.gid && gui.goTo(`/voter-application/${row.gid}`)}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={6} className={styles.emptyTable}>{t("reporting.noApplicantsSelection")}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const OperatorReport = ({ gui, ninu }) => {
  const [report, setReport] = React.useState(null);
  const [day, setDay] = React.useState(null);
  const [selectedDate, setSelectedDate] = React.useState("");
  const [selectedHour, setSelectedHour] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [dayLoading, setDayLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await rest.request(
          getServiceUri() + `reporting/operator-reports/${encodeURIComponent(ninu)}`,
          "GET"
        );

        if (cancelled) {
          return;
        }

        setReport(response || null);
        setSelectedDate(datePart(response?.lastSubmissionAt) || datePart(response?.firstSubmissionAt) || "");
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || String(e));
          setReport(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [ninu]);

  React.useEffect(() => {
    if (!selectedDate) {
      setDay(null);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setDayLoading(true);
      setSelectedHour(null);

      try {
        const response = await rest.request(
          getServiceUri()
            + `reporting/operator-reports/${encodeURIComponent(ninu)}`
            + `/day/${encodeURIComponent(selectedDate)}`,
          "GET"
        );

        if (!cancelled) {
          setDay(response || null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || String(e));
          setDay(null);
        }
      } finally {
        if (!cancelled) {
          setDayLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [ninu, selectedDate]);

  if (loading) {
    return <div className={styles.loading}>{t("reporting.loadingOperator")}</div>;
  }

  if (error && !report) {
    return <div className={styles.error}>{error}</div>;
  }

  if (!report) {
    return <div className={styles.error}>{t("reporting.operatorNotFound")}</div>;
  }

  const averagePerActiveDay = report.activeDayCount
    ? Number(report.applicationCount || 0) / Number(report.activeDayCount)
    : 0;

  const busiestDay = (report.days || []).reduce(
    (best, row) => Number(row.count || 0) > Number(best?.count || 0) ? row : best,
    null
  );

  const firstActivityDate = datePart(report.firstSubmissionAt);
  const lastActivityDate = datePart(report.lastSubmissionAt);
  const canGoPrevious = Boolean(selectedDate && firstActivityDate && selectedDate > firstActivityDate);
  const canGoNext = Boolean(selectedDate && lastActivityDate && selectedDate < lastActivityDate);

  return (
    <div className={styles.page}>
      <OperatorHero report={report} gui={gui} />

      {error ? <div className={styles.errorInline}>{error}</div> : null}

      <div className={styles.metrics}>
        <Metric label={t("reporting.applicants")} value={Number(report.applicationCount || 0).toLocaleString()} />
        <Metric label={t("reporting.activeDays")} value={Number(report.activeDayCount || 0).toLocaleString()} />
        <Metric label={t("reporting.perActiveDay")} value={averagePerActiveDay.toFixed(1)} />
        <Metric
          label={t("reporting.busiestDay")}
          value={Number(busiestDay?.count || 0).toLocaleString()}
          detail={busiestDay?.date || "—"}
        />
        <Metric label={t("reporting.votingCenters")} value={Number(report.votingCenterCount || 0).toLocaleString()} />
      </div>

      <AccountsPanel accounts={report.accounts} gui={gui} />

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <div className={styles.panelEyebrow}>{t("reporting.lifetimeHistory")}</div>
            <h2 className={styles.panelTitle}>{t("reporting.applicantsByDay")}</h2>
            <div className={styles.panelSubtitle}>
              {t("reporting.scrollZoomHistoryHelp")}
            </div>
          </div>
        </div>
        <DailyHistory report={report} selectedDate={selectedDate} onSelectDate={setSelectedDate} />
      </section>

      <section className={`${styles.panel} ${styles.dayPanel}`}>
        <div className={styles.dayHeader}>
          <IconButton
            size="small"
            disabled={!canGoPrevious || dayLoading}
            onClick={() => setSelectedDate((value) => shiftDate(value, -1))}
          >
            <ChevronLeftIcon />
          </IconButton>

          <div className={styles.dayTitleBlock}>
            <div className={styles.panelEyebrow}>{t("reporting.dailyInspector")}</div>
            <h2 className={styles.dayTitle}>{formatDateLabel(selectedDate)}</h2>
          </div>

          <IconButton
            size="small"
            disabled={!canGoNext || dayLoading}
            onClick={() => setSelectedDate((value) => shiftDate(value, 1))}
          >
            <ChevronRightIcon />
          </IconButton>
        </div>

        {dayLoading ? (
          <div className={styles.dayLoading}>{t("reporting.loadingDay")}</div>
        ) : (
          <>
            <div className={styles.dayStats}>
              <Metric label={t("reporting.applicants")} value={Number(day?.applicationCount || 0).toLocaleString()} />
              <Metric label={t("common.first")} value={formatTime(day?.firstSubmissionAt)} />
              <Metric label={t("common.last")} value={formatTime(day?.lastSubmissionAt)} />
            </div>

            <div className={styles.chartSectionTitle}>{t("reporting.distributionDay")}</div>
            <div className={styles.chartSectionSubtitle}>
              {t("reporting.hourFilterHelp")}
            </div>
            <HourlyHistogram
              hours={day?.hours || []}
              selectedHour={selectedHour}
              onSelectHour={setSelectedHour}
            />

            <div className={styles.chartSectionTitle}>{t("reporting.applicantCadence")}</div>
            <div className={styles.chartSectionSubtitle}>
              {t("reporting.cadenceHelp")}
            </div>
            <CadenceChart applications={distinctApplications(day?.applications || [])} gui={gui} />
          </>
        )}
      </section>

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <div className={styles.panelEyebrow}>{t("reporting.locationFootprint")}</div>
            <h2 className={styles.panelTitle}>{t("reporting.applicantsByCenter")}</h2>
          </div>
        </div>
        <CentersChart rows={report.votingCenters || []} />
      </section>

      <DayApplications
        day={day}
        selectedHour={selectedHour}
        gui={gui}
        onClearHour={() => setSelectedHour(null)}
      />
    </div>
  );
};

export const displayOperatorReport = (gui, ninu) => ({
  uuid: `operator-report-${ninu}`,
  view: () => <OperatorReport gui={gui} ninu={ninu} />,
});

export default displayOperatorReport;
